import { useState } from 'react'
import { createPortal } from 'react-dom'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import StockReferenceSelectInput from '@/components/inventory/StockReferenceSelectInput'
import { addDetail, CreateStockDispatchDetailPayload } from '@/services/api/stockDispatch'

interface AddSubDataModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  stockDispatchId?: string
}

const AddSubDataModal = ({ isOpen, onClose, onSuccess, stockDispatchId }: AddSubDataModalProps) => {
  const [formData, setFormData] = useState<CreateStockDispatchDetailPayload>({
    bags: undefined,
    tonnage: '',
    lotNumber: '',
    phPi: '',
    contractNumber: '',
    bookingNumber: '',
    product: '',
    forwarder: '',
    container: '',
    receivedDate: '',
    freeStorageDays: undefined,
    storageStartDate: '',
    dispatchedDate: '',
    destination: '',
    transporter: '',
    truckRate: '',
    truckReg: '',
    invoiceNumber: '',
    chargeableDays: undefined,
    daysInStock: undefined,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseInt(value) : undefined) : value
    }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!stockDispatchId) {
      setError('Stock dispatch ID is required')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const payload: CreateStockDispatchDetailPayload = {
        bags: formData.bags,
        tonnage: formData.tonnage || undefined,
        lotNumber: formData.lotNumber || undefined,
        phPi: formData.phPi || undefined,
        contractNumber: formData.contractNumber || undefined,
        bookingNumber: formData.bookingNumber || undefined,
        product: formData.product || undefined,
        forwarder: formData.forwarder || undefined,
        container: formData.container || undefined,
        receivedDate: formData.receivedDate || undefined,
        freeStorageDays: formData.freeStorageDays,
        storageStartDate: formData.storageStartDate || undefined,
        dispatchedDate: formData.dispatchedDate || undefined,
        destination: formData.destination || undefined,
        transporter: formData.transporter || undefined,
        truckRate: formData.truckRate || undefined,
        truckReg: formData.truckReg || undefined,
        invoiceNumber: formData.invoiceNumber || undefined,
        chargeableDays: formData.chargeableDays,
        daysInStock: formData.daysInStock,
      }

      await addDetail(stockDispatchId, payload)
      
      // Reset form
      setFormData({
        bags: undefined,
        tonnage: '',
        lotNumber: '',
        phPi: '',
        contractNumber: '',
        bookingNumber: '',
        product: '',
        forwarder: '',
        container: '',
        receivedDate: '',
        freeStorageDays: undefined,
        storageStartDate: '',
        dispatchedDate: '',
        destination: '',
        transporter: '',
        truckRate: '',
        truckReg: '',
        invoiceNumber: '',
        chargeableDays: undefined,
        daysInStock: undefined,
      })
      
      onSuccess?.()
    } catch (err: any) {
      setError(err.message || 'Failed to add detail')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      bags: undefined,
      tonnage: '',
      lotNumber: '',
      phPi: '',
      contractNumber: '',
      bookingNumber: '',
      product: '',
      forwarder: '',
      container: '',
      receivedDate: '',
      freeStorageDays: undefined,
      storageStartDate: '',
      dispatchedDate: '',
      destination: '',
      transporter: '',
      truckRate: '',
      truckReg: '',
      invoiceNumber: '',
      chargeableDays: undefined,
      daysInStock: undefined,
    })
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end" onClick={handleClose} style={{ zIndex: 60 }}>
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '700px',
          maxWidth: '700px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px'
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', lineHeight: '26px', color: '#212126' }}>
            Add Dispatch Detail
          </h2>
          <button
            onClick={handleClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ width: '24px', height: '24px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form id="add-subdata-form" onSubmit={handleSubmit} className="flex-1" style={{ padding: '24px', overflow: 'auto' }}>
          {error && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#E93323' }}>
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Row 1: Bags, Tonnage, Lot Number */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Bags
                </label>
                <input
                  type="number"
                  name="bags"
                  value={formData.bags ?? ''}
                  onChange={handleChange}
                  placeholder="Enter bags"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Tonnage
                </label>
                <input
                  type="text"
                  name="tonnage"
                  value={formData.tonnage as string}
                  onChange={handleChange}
                  placeholder="Enter tonnage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Lot Number
                </label>
                <StockReferenceSelectInput
                  type="lot"
                  value={formData.lotNumber || ''}
                  onChange={(lotNumber) => setFormData((prev) => ({ ...prev, lotNumber }))}
                />
              </div>
            </div>

            {/* Row 2: PH/PI, Contract Number, Booking Number */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  PH / PI
                </label>
                <input
                  type="text"
                  name="phPi"
                  value={formData.phPi}
                  onChange={handleChange}
                  placeholder="Enter PH/PI"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Contract Number
                </label>
                <input
                  type="text"
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                  placeholder="Enter contract number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Booking Number
                </label>
                <StockReferenceSelectInput
                  type="booking"
                  value={formData.bookingNumber || ''}
                  onChange={(bookingNumber) => setFormData((prev) => ({ ...prev, bookingNumber }))}
                />
              </div>
            </div>

            {/* Row 3: Product, Forwarder, Container */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Product
                </label>
                <ProductSelectInput
                  value={formData.product || ''}
                  onChange={(product) => setFormData((prev) => ({ ...prev, product }))}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Forwarder
                </label>
                <input
                  type="text"
                  name="forwarder"
                  value={formData.forwarder}
                  onChange={handleChange}
                  placeholder="Enter forwarder"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Container
                </label>
                <input
                  type="text"
                  name="container"
                  value={formData.container}
                  onChange={handleChange}
                  placeholder="Enter container"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            {/* Row 4: Received Date, Free Storage Days, Storage Start */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Received Date
                </label>
                <input
                  type="date"
                  name="receivedDate"
                  value={formData.receivedDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Free Storage Days
                </label>
                <input
                  type="number"
                  name="freeStorageDays"
                  value={formData.freeStorageDays ?? ''}
                  onChange={handleChange}
                  placeholder="Days"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Storage Start Date
                </label>
                <input
                  type="date"
                  name="storageStartDate"
                  value={formData.storageStartDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            {/* Row 5: Dispatched Date, Destination, Transporter */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Dispatched Date
                </label>
                <input
                  type="date"
                  name="dispatchedDate"
                  value={formData.dispatchedDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Destination
                </label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  placeholder="Enter destination"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Transporter
                </label>
                <input
                  type="text"
                  name="transporter"
                  value={formData.transporter}
                  onChange={handleChange}
                  placeholder="Enter transporter"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            {/* Row 6: Truck Rate, Truck Reg, Invoice Number */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Truck Rate
                </label>
                <input
                  type="text"
                  name="truckRate"
                  value={formData.truckRate as string}
                  onChange={handleChange}
                  placeholder="Enter truck rate"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Truck Reg
                </label>
                <input
                  type="text"
                  name="truckReg"
                  value={formData.truckReg}
                  onChange={handleChange}
                  placeholder="Enter truck reg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Invoice Number
                </label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder="Enter invoice number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            {/* Row 7: Chargeable Days, Days in Stock */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Chargeable Days
                </label>
                <input
                  type="number"
                  name="chargeableDays"
                  value={formData.chargeableDays ?? ''}
                  onChange={handleChange}
                  placeholder="Days"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Days in Stock
                </label>
                <input
                  type="number"
                  name="daysInStock"
                  value={formData.daysInStock ?? ''}
                  onChange={handleChange}
                  placeholder="Days"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>
          </div>
        </form>

        <div
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderRadius: '8px' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-subdata-form"
            disabled={isSubmitting}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#FFFFFF', backgroundColor: '#212126', borderRadius: '8px' }}
          >
            {isSubmitting ? 'Adding...' : 'Add Detail'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AddSubDataModal
